import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import CampaignIcon from "@mui/icons-material/Campaign";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import HotelIcon from "@mui/icons-material/Hotel";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SchoolIcon from "@mui/icons-material/School";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SupportIcon from "@mui/icons-material/Support";
import {
  Alert,
  Avatar,
  Box,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import React, { memo, useCallback } from "react";
import { CampaignTemplate, Template } from "../../../interfaces/all";

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 12px 16px;
  margin-bottom: 1px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-size: 14px;
  color: #008069;
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TemplateItem = styled(ListItem)<{ status: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 4px;
  background: transparent;
  box-shadow: none;
  transition: background 0.2s;
  cursor: pointer;
  border-left: ${(props) => {
    switch (props.status) {
      case "APPROVED":
        return "4px solid #4caf50";
      case "PENDING":
        return "4px solid #ff9800";
      case "REJECTED":
        return "4px solid #f44336";
      default:
        return "4px solid #9e9e9e";
    }
  }};
  &:hover {
    background-color: #f5f6f6;
  }
`;

const CampaignCard = styled(Box)({
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  padding: "12px",
  marginBottom: "8px",
  backgroundColor: "#fafbfc",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f0f7ff",
    borderColor: "#2196f3",
  },
  "&.selected": {
    backgroundColor: "#e3f2fd",
    borderColor: "#2196f3",
    borderWidth: "2px",
  },
});

const EmptyState = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 16px",
  textAlign: "center",
  color: "#667781",
});

const EmptyStateIcon = styled(Box)({
  marginBottom: "16px",
  color: "#bdbdbd",
  fontSize: "48px",
});

const CategoryIcon = styled(Avatar)<{ category: string }>`
  width: 28px;
  height: 28px;
  font-size: 14px;
  background-color: ${(props) => {
    switch (props.category.toLowerCase()) {
      case "marketing":
        return "#008069";
      case "utility":
        return "#4caf50";
      case "authentication":
        return "#ff9800";
      case "support":
        return "#9c27b0";
      case "finance":
        return "#f44336";
      case "shopping":
        return "#e91e63";
      case "travel":
        return "#00bcd4";
      case "healthcare":
        return "#8bc34a";
      case "education":
        return "#795548";
      case "food":
        return "#ff5722";
      case "real_estate":
        return "#607d8b";
      default:
        return "#008069";
    }
  }};
  color: white;
  margin-right: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
`;

const LanguageFlag = styled(Avatar)<{ language: string }>`
  width: 28px;
  height: 28px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) => {
    switch (props.language.toLowerCase()) {
      case "pt_br":
      case "pt":
        return "#009c3b";
      case "en":
      case "en_us":
        return "#1e3a8a";
      case "es":
      case "es_es":
        return "#dc2626";
      case "fr":
      case "fr_fr":
        return "#1e40af";
      case "de":
      case "de_de":
        return "#dc2626";
      case "it":
      case "it_it":
        return "#059669";
      case "ar":
        return "#059669";
      case "hi":
        return "#dc2626";
      case "ja":
        return "#dc2626";
      case "ko":
        return "#1e40af";
      case "zh":
      case "zh_cn":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  }};
  color: white;
  margin-right: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
`;

const TemplateName = styled(Typography)`
  font-size: 15px;
  font-weight: 500;
  color: #111b21;
  line-height: 1.4;
  margin-bottom: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: #008069;
  }
`;

const TemplateMeta = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const MetaItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background-color: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    transform: translateY(-1px);
  }
`;

const MetaText = styled(Typography)`
  font-size: 11px;
  color: #667781;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Funções auxiliares para formatação
const getCategoryName = (category: string) => {
  switch (category.toLowerCase()) {
    case "marketing":
      return "Marketing";
    case "utility":
      return "Utilitário";
    case "authentication":
      return "Autenticação";
    case "support":
      return "Suporte";
    case "finance":
      return "Financeiro";
    case "shopping":
      return "Compras";
    case "travel":
      return "Viagem";
    case "healthcare":
      return "Saúde";
    case "education":
      return "Educação";
    case "food":
      return "Alimentação";
    case "real_estate":
      return "Imobiliário";
    case "hotel":
      return "Hotelaria";
    default:
      return category;
  }
};

const getLanguageName = (language: string) => {
  switch (language.toLowerCase()) {
    case "pt_br":
    case "pt":
      return "Português (BR)";
    case "en":
    case "en_us":
      return "English (US)";
    case "es":
    case "es_es":
      return "Español (ES)";
    case "fr":
    case "fr_fr":
      return "Français (FR)";
    case "de":
    case "de_de":
      return "Deutsch (DE)";
    case "it":
    case "it_it":
      return "Italiano (IT)";
    case "ar":
      return "العربية (AR)";
    case "hi":
      return "हिन्दी (IN)";
    case "ja":
      return "日本語 (JP)";
    case "ko":
      return "한국어 (KR)";
    case "zh":
    case "zh_cn":
      return "中文 (CN)";
    default:
      return language;
  }
};

interface TemplateSelectorProps {
  campaigns: any[];
  templates: Template[];
  campaignTemplates: CampaignTemplate[];
  selectedCampaign: string;
  selectedTemplate: Template | null;
  loading: boolean;
  error: string | null;
  onCampaignChange: (campaignId: string) => void;
  onTemplateSelect: (template: Template) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = memo(
  ({
    campaigns,
    templates,
    campaignTemplates,
    selectedCampaign,
    selectedTemplate,
    loading,
    error,
    onCampaignChange,
    onTemplateSelect,
  }) => {
    const getAssignedTemplates = useCallback(
      (campaignId: string) => {
        if (!campaignId || !campaignTemplates.length) return [];
        return campaignTemplates.filter(
          (ct) => ct.campaign_id === campaignId && ct.is_active,
        );
      },
      [campaignTemplates],
    );

    const getCategoryIcon = useCallback((category: string) => {
      switch (category?.toLowerCase()) {
        case "marketing":
          return <CampaignIcon fontSize="small" />;
        case "utility":
          return <NotificationsIcon fontSize="small" />;
        case "authentication":
          return <SupportIcon fontSize="small" />;
        case "support":
          return <SupportIcon fontSize="small" />;
        case "finance":
          return <AccountBalanceIcon fontSize="small" />;
        case "shopping":
          return <ShoppingCartIcon fontSize="small" />;
        case "travel":
          return <LocalShippingIcon fontSize="small" />;
        case "healthcare":
          return <HealthAndSafetyIcon fontSize="small" />;
        case "education":
          return <SchoolIcon fontSize="small" />;
        case "food":
          return <RestaurantIcon fontSize="small" />;
        case "real_estate":
          return <BusinessIcon fontSize="small" />;
        case "hotel":
          return <HotelIcon fontSize="small" />;
        default:
          return <BusinessIcon fontSize="small" />;
      }
    }, []);

    const getLanguageFlag = useCallback((language: string) => {
      switch (language?.toLowerCase()) {
        case "pt_br":
        case "pt":
          return "BR";
        case "en":
        case "en_us":
          return "US";
        case "es":
        case "es_es":
          return "ES";
        case "fr":
        case "fr_fr":
          return "FR";
        case "de":
        case "de_de":
          return "DE";
        case "it":
        case "it_it":
          return "IT";
        case "ar":
          return "AR";
        case "hi":
          return "IN";
        case "ja":
          return "JP";
        case "ko":
          return "KR";
        case "zh":
        case "zh_cn":
          return "CN";
        default:
          return language?.toUpperCase().substring(0, 2) || "PT";
      }
    }, []);

    if (loading) {
      return (
        <>
          <BoxWrapper>
            <SectionTitle>
              <CampaignIcon fontSize="small" />
              Selecionar Campanha
            </SectionTitle>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}
            >
              {/* Select de campanha */}
              <Skeleton
                variant="rectangular"
                height={40}
                sx={{ borderRadius: "8px" }}
              />

              {/* Card de resumo da campanha selecionada */}
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "#fafbfc",
                }}
              >
                <Skeleton
                  variant="text"
                  width="70%"
                  height={22}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton variant="text" width="55%" height={18} />
              </Box>
            </Box>
          </BoxWrapper>

          <BoxWrapper>
            <SectionTitle>
              <CampaignIcon fontSize="small" />
              Selecionar Template
            </SectionTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[...Array(4)].map((_, index) => (
                <Box
                  key={`template-selector-skeleton-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderBottom: "1px solid #f0f0f0",
                    borderRadius: "6px",
                    background: "transparent",
                    borderLeft: "4px solid #e0e0e0",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton
                      variant="text"
                      width={`${70 - index * 6}%`}
                      height={20}
                      sx={{ mb: 0.5 }}
                    />
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Skeleton
                        variant="rounded"
                        width={110}
                        height={26}
                        sx={{ borderRadius: "13px" }}
                      />
                      <Skeleton
                        variant="rounded"
                        width={140}
                        height={26}
                        sx={{ borderRadius: "13px" }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </BoxWrapper>
        </>
      );
    }

    if (error) {
      return (
        <BoxWrapper>
          <SectionTitle>
            <CampaignIcon fontSize="small" />
            Selecionar Campanha
          </SectionTitle>
          <Alert
            severity="error"
            sx={{
              mt: 2,
              "& .MuiAlert-icon": {
                color: "#d32f2f",
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Erro ao carregar dados
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
              {error}
            </Typography>
          </Alert>
        </BoxWrapper>
      );
    }

    return (
      <>
        {/* Seleção de Campanha */}
        <BoxWrapper>
          <SectionTitle>
            <CampaignIcon fontSize="small" />
            Selecionar Campanha
          </SectionTitle>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Campanha</InputLabel>
            <Select
              value={selectedCampaign}
              onChange={(e) => onCampaignChange(e.target.value)}
              label="Campanha"
              sx={{ borderRadius: "8px" }}
            >
              {campaigns && campaigns.length > 0 ? (
                campaigns
                  .filter(
                    (campaign) => campaign && campaign.id && campaign.name,
                  )
                  .map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))
              ) : (
                <MenuItem key="no-campaigns" disabled>
                  Nenhuma campanha disponível
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {selectedCampaign && (
            <CampaignCard>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#111b21", mb: 1 }}
              >
                {campaigns.find((c) => c.id === selectedCampaign)?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Templates atribuídos:{" "}
                {selectedCampaign
                  ? getAssignedTemplates(selectedCampaign).length
                  : 0}
              </Typography>
            </CampaignCard>
          )}
        </BoxWrapper>

        {/* Seleção de Template */}
        {selectedCampaign && (
          <BoxWrapper>
            <SectionTitle>
              <CampaignIcon fontSize="small" />
              Selecionar Template
            </SectionTitle>

            {getAssignedTemplates(selectedCampaign).length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>
                  <CampaignIcon />
                </EmptyStateIcon>
                <Typography variant="h6" sx={{ mb: 1, color: "#111b21" }}>
                  Nenhum template atribuído
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#667781", textAlign: "center" }}
                >
                  Esta campanha não possui templates atribuídos.
                </Typography>
              </EmptyState>
            ) : (
              <List>
                {getAssignedTemplates(selectedCampaign).map(
                  (campaignTemplate) => {
                    const template = templates.find(
                      (t) => t.id === campaignTemplate.template_id,
                    );
                    if (!template) return null;

                    return (
                      <TemplateItem
                        key={campaignTemplate.template_id}
                        status={template.status}
                        onClick={() => onTemplateSelect(template)}
                        sx={{
                          backgroundColor:
                            selectedTemplate?.id === template.id
                              ? "#e3f2fd"
                              : "transparent",
                          borderRight:
                            selectedTemplate?.id === template.id
                              ? "2px solid #2196f3"
                              : "1px solid #f0f0f0",
                          borderTop:
                            selectedTemplate?.id === template.id
                              ? "2px solid #2196f3"
                              : "1px solid #f0f0f0",
                          borderBottom:
                            selectedTemplate?.id === template.id
                              ? "2px solid #2196f3"
                              : "1px solid #f0f0f0",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <TemplateName>{template.name}</TemplateName>
                          </Box>
                          <TemplateMeta>
                            <MetaItem>
                              <CategoryIcon
                                category={template.category || "default"}
                              >
                                {getCategoryIcon(
                                  template.category || "default",
                                )}
                              </CategoryIcon>
                              <MetaText>
                                {getCategoryName(
                                  template.category || "default",
                                )}
                              </MetaText>
                            </MetaItem>
                            <MetaItem>
                              <LanguageFlag
                                language={template.language || "pt"}
                              >
                                {getLanguageFlag(template.language || "pt")}
                              </LanguageFlag>
                              <MetaText>
                                {getLanguageName(template.language || "pt")}
                              </MetaText>
                            </MetaItem>
                          </TemplateMeta>
                        </Box>
                      </TemplateItem>
                    );
                  },
                )}
              </List>
            )}
          </BoxWrapper>
        )}
      </>
    );
  },
);

TemplateSelector.displayName = "TemplateSelector";

export default TemplateSelector;
